import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PlainTextProps } from "../../interfaces";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'; // Add this import

const PlainText: React.FC<PlainTextProps> = ({
    // Content
    title = "Terms of Use",
    titleHighlight = "EPAYR Website",
    sections = [],
    
    // Styling
    containerSx,
    titleSx,
    sectionSx,
    sectionTitleSx,
    sectionContentSx={textTransform: "uppercase"},
    
    // Colors
    primaryColor = "darkPurple.500",
    secondaryColor = "lightPurple.500",
    backgroundColor = "beige.100",
    
    // Layout
    spacing = {md: "80px", xs: "56px"},
    sectionSpacing = {md: "80px", xs: "56px"},
    contentSpacing = {mdd: "16px", xs: "12px"}
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showScrollUp, setShowScrollUp] = useState(false);
    
    useEffect(() => {
      const handleScroll = () => {
        setShowScrollUp(window.scrollY > 300);
      };
      
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };
    return (
      <Box sx={{
        display: "flex",
        padding: {md: "0 80px", xs: "0px 10px 12px 10px"},
        margin: "0 auto",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "1440px",
        width: "100%",
        gap: spacing,
        backgroundColor: backgroundColor,
        ...containerSx
      }}>
        {title && (
          <Typography variant="h3" sx={{
            color: primaryColor,
            textAlign: "center",
            ...titleSx
          }}>
            {title} <br/>
            {titleHighlight && (
              <Box component="span" sx={{color: secondaryColor}}>
                {titleHighlight}
              </Box>
            )}
          </Typography>
        )}
        
        <Stack sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: sectionSpacing,
          width: "100%"
        }}>
          {sections.map((section) => (
            <Box key={section.id} sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "32px",
              width: "100%",
              marginTop: 0,
              ...sectionSx
            }}>
              <Box>
                <Typography variant="subtitle8" sx={{
                  textTransform: "uppercase",
                  wordBreak: "break-word",
                  ...sectionTitleSx
                }}>
                  <Box component="span" sx={{ 
                    color: section.titleReverse ? secondaryColor : primaryColor 
                  }}>
                    {section.showId ? section.id + '.' : ''} {section.title}{" "}
                  </Box>
                  {section.titleHighlight && (
                    <Box component="span" sx={{ 
                      color: section.titleReverse ? primaryColor : secondaryColor 
                    }}>
                      {section.titleHighlight}
                    </Box>
                  )}
                </Typography>
              </Box>
              
              <Stack sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: contentSpacing,
                alignSelf: "stretch",
                ...sectionContentSx
              }}>
                {section.content.map((item, index) =>
                  <Typography key={index} variant="subtitle1" sx={{
                    ...sectionContentSx
                  }}> 
                    <Box component="span" 
                    sx={{
                              paddingRight: {md: "0px", xs: "10px"},
                    }}
                    dangerouslySetInnerHTML={{ __html: item }}/>
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
        {isMobile && showScrollUp && (
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              backgroundColor: primaryColor,
              color: "white",
              "&:hover": {
                backgroundColor: secondaryColor,
              },
              zIndex: 999,
              width: "48px",
              height: "48px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)"
            }}
          >
            <KeyboardArrowUpIcon fontSize="medium" />
          </IconButton>
        )}
      </Box>
    );
  };

export default PlainText;
